/**
 * S3 Operation Counter Utility
 * 
 * This utility helps track and monitor AWS S3 operations
 * to help stay within free tier limits.
 */

// Track operations by type
interface S3OperationCounts {
  get: number;
  put: number;
  list: number;
  delete: number;
  total: number;
}

// Store the counts in localStorage to persist between sessions
const STORAGE_KEY = 'mane_s3_operation_counts';
const RESET_DATE_KEY = 'mane_s3_reset_date';

// Initialize or load existing counts
export const getOperationCounts = (): S3OperationCounts => {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch (e) {
      console.error('Failed to parse stored S3 operation counts', e);
    }
  }
  
  return {
    get: 0,
    put: 0,
    list: 0,
    delete: 0,
    total: 0
  };
};

// Track an operation
export const trackOperation = (type: 'get' | 'put' | 'list' | 'delete'): void => {
  const counts = getOperationCounts();
  counts[type]++;
  counts.total++;
  
  localStorage.setItem(STORAGE_KEY, JSON.stringify(counts));
  
  // Check if we should reset (first of month)
  checkMonthlyReset();
};

// Check if we should reset counts (at beginning of month)
const checkMonthlyReset = (): void => {
  const now = new Date();
  const currentMonth = `${now.getFullYear()}-${now.getMonth() + 1}`;
  const storedMonth = localStorage.getItem(RESET_DATE_KEY);
  
  if (storedMonth !== currentMonth) {
    // New month, reset counts
    const counts = {
      get: 0,
      put: 0,
      list: 0,
      delete: 0,
      total: 0
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(counts));
    localStorage.setItem(RESET_DATE_KEY, currentMonth);
    
    console.info(`S3 operation counts reset for new month: ${currentMonth}`);
  }
};

// Check if we're approaching free tier limits
export const checkFreeTierLimits = (): {
  isApproachingLimit: boolean;
  message: string | null;
} => {
  const counts = getOperationCounts();
  
  // Free tier limits
  const GET_LIMIT = 20000;
  const PUT_LIST_LIMIT = 2000;
  
  const getPercentage = Math.floor((counts.get / GET_LIMIT) * 100);
  const putListPercentage = Math.floor(
    ((counts.put + counts.list) / PUT_LIST_LIMIT) * 100
  );
  
  if (getPercentage > 80 || putListPercentage > 80) {
    return {
      isApproachingLimit: true,
      message: `Warning: Approaching AWS free tier limits. GET: ${getPercentage}%, PUT/LIST: ${putListPercentage}%`
    };
  }
  
  return {
    isApproachingLimit: false,
    message: null
  };
};

// Add a middleware wrapper for S3 operations
export const withTracking = <T>(
  operation: () => Promise<T>,
  type: 'get' | 'put' | 'list' | 'delete'
): Promise<T> => {
  trackOperation(type);
  return operation();
};

// Reset the counter (for testing)
export const resetCounter = (): void => {
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(RESET_DATE_KEY);
}; 